import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import InvoiceUploader, { InvoiceLinks } from "@/components/InvoiceUploader";
import TaxBlock from "@/components/TaxBlock";
import { useAuth } from "@/context/AuthContext";
import { Plus, Receipt, Check, ThumbsUp, ThumbsDown, CreditCard, X, Building2 } from "lucide-react";
import { ExpenseLinesNotice } from "@/components/ExpenseLinesNotice";
import { ChainButton } from "@/components/ApprovalChain";
import ChainActionDialog from "@/components/ChainActionDialog";
import ExpenseEditForm from "@/components/ExpenseEditForm";
import { RejectedNote } from "@/components/EditResubmit";
import { ExpensePayDialog, ExpensePayCorrectDialog } from "@/components/ExpensePayDialog";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const statusTone = { draft: "slate", submitted: "amber", verified: "sky", approved: "emerald", rejected: "rose", paid: "emerald" };

function monthsList(count = 24) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < count; i++) {
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0");
    out.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return out;
}

export default function ExpensesV2() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [monthFilter, setMonthFilter] = useState("");
  const [tab, setTab] = useState(user?.role === "Employee" ? "mine" : "all");
  const [target, setTarget] = useState(null);
  const [payOn, setPayOn] = useState(null);
  const [correctOn, setCorrectOn] = useState(null);
  const isAdmin = [user?.role, ...(user?.roles || [])].includes("Admin");
  const canCorrect = (r) => {
    const last = (r.payments || []).slice(-1)[0];
    return !!last && (isAdmin || (Date.now() - new Date(last.at).getTime()) / 86400000 <= 7);
  };
  const [editExp, setEditExp] = useState(null);
  const closeEdit = () => { setEditExp(null); window.history.replaceState(null, "", "/app/expenses-v2"); };
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const id = qs.get("edit") || qs.get("copy");
    if (id) api.get(`/v2/expenses/${id}`).then(r => {
      const e = r.data;
      if (e.requester_id !== user?.id) return;
      if (qs.get("edit") && e.status === "sent_back") setEditExp({ exp: e, mode: "edit" });
      if (qs.get("copy") && e.status === "rejected") setEditExp({ exp: e, mode: "copy" });
    }).catch(() => {});
  }, []);

  const load = () => {
    const params = new URLSearchParams();
    if (tab === "mine") params.set("mine", "true");
    if (monthFilter) params.set("expense_month", monthFilter);
    api.get(`/v2/expenses?${params}`).then(r => setRows(r.data));
  };
  useEffect(load, [tab, monthFilter]);

  const isAccounts = ["Admin", "Accounts"].includes(user?.role);
  const isApprover = ["Admin", "Approver", "Authorizer"].includes(user?.role);

  const action = async (id, path, payload = {}) => {
    try { await api.post(`/v2/expenses/${id}/${path}`, payload); toast.success(`${path} done`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doReject = async (id) => {
    const reason = window.prompt("Reason for rejection?");
    if (!reason) return;
    action(id, "reject", { reason });
  };

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="expenses-v2-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Receipt className="w-6 h-6" /> Project Expenses</h1>
          <p className="text-sm text-slate-500 mt-1">Vendor bills · GST + TDS aware · Invoice Total charged to working budget</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={monthFilter || "all"} onValueChange={(v) => setMonthFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="expenses-month-filter"><SelectValue placeholder="All months" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {monthsList().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tab} onValueChange={setTab}>
            <SelectTrigger className="w-[140px]" data-testid="expenses-scope"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="mine">Mine</SelectItem></SelectContent>
          </Select>
          <Button onClick={() => setCreateOpen(true)} data-testid="new-expense-btn"><Plus className="w-4 h-4 mr-1" />New expense</Button>
        </div>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr className="border-b">
              <th className="text-left px-3 py-2">Month</th>
              <th className="text-left px-3 py-2">Project · Sub-Type</th>
              <th className="text-left px-3 py-2">Vendor · Invoice</th>
              <th className="text-right px-3 py-2">Invoice Total</th>
              <th className="text-right px-3 py-2">Net Payable</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-slate-500">No expenses yet</td></tr>}
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b hover:bg-slate-50" data-testid={`expense-row-${i}`}>
                <td className="px-3 py-2 text-xs font-mono">{r.expense_month}</td>
                <td className="px-3 py-2"><span className="font-mono text-xs">{r.project_code}</span> · <span className="font-mono text-xs">{r.subtype_code}</span><div className="text-[11px] text-slate-500">{r.subtype_name}</div></td>
                <td className="px-3 py-2"><div className="text-sm">{r.vendor_name}</div><div className="text-[10px] text-slate-500">{r.invoice_no}</div></td>
                <td className="px-3 py-2 text-right font-mono">{rupee(r.invoice_total)}</td>
                <td className="px-3 py-2 text-right font-mono">{rupee(r.net_payable)}</td>
                <td className="px-3 py-2">
                  <Badge className={`bg-${statusTone[r.status]}-100 text-${statusTone[r.status]}-800 border-${statusTone[r.status]}-300 text-[10px]`} variant="outline">{r.status === "sent_back" ? "Sent back – awaiting requester" : r.status}</Badge>
                  {r.inv_ref && <span className="ml-1 font-mono text-[10px] text-indigo-700" data-testid={`exp-inv-${i}`}>{r.inv_ref}</span>}
                  {r.bill_file_id && <InvoiceLinks fileIds={[r.bill_file_id]} compact />}
                </td>
                <td className="px-3 py-2 flex gap-1">
                  {r.status === "draft" && r.requester_id === user?.id && <Button size="sm" variant="outline" onClick={() => action(r.id, "submit")} data-testid={`submit-${i}`}>Submit</Button>}
                  {r.status === "authorized" && isAccounts && <Button size="sm" variant="outline" onClick={() => setTarget({ kind: "expense", id: r.id })} data-testid={`verify-${i}`}><ThumbsUp className="w-3 h-3 mr-1" />Verify</Button>}
                  {["submitted", "endorsed", "verified"].includes(r.status) && <Button size="sm" onClick={() => setTarget({ kind: "expense", id: r.id })} data-testid={`approve-${i}`}><Check className="w-3 h-3 mr-1" />{r.status === "submitted" ? "Endorse" : r.status === "endorsed" ? "Authorize" : "Final approve"}</Button>}
                  {r.status === "approved" && isAccounts && <Button size="sm" variant="outline" onClick={() => setPayOn(r)} data-testid={`pay-${i}`}><CreditCard className="w-3 h-3 mr-1" />{r.payee_user_id && r.payee_user_id === user?.id ? "Pay (own)" : "Pay"}</Button>}
                  {r.status === "approved" && Number(r.paid_amount || 0) > 0 && <span className="text-[10px] text-amber-800" data-testid={`part-paid-${i}`}>Part-paid · balance {rupee(r.balance_payable)}</span>}
                  {r.self_disbursed && <Badge className="text-[10px] bg-amber-100 text-amber-900 border-amber-300" data-testid={`self-paid-${i}`}>Self-paid (Accounts)</Badge>}
                  {isAccounts && canCorrect(r) && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setCorrectOn(r)} data-testid={`pay-correct-${i}`}>Correct payment</Button>}
                  {["submitted", "endorsed", "authorized", "verified"].includes(r.status) && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => doReject(r.id)} data-testid={`reject-${i}`}><ThumbsDown className="w-3 h-3" /></Button>}
                  {r.status === "sent_back" && r.requester_id === user?.id && <Button size="sm" className="bg-rose-600 hover:bg-rose-700" onClick={() => setEditExp({ exp: r, mode: "edit" })} data-testid={`exp-edit-btn-${i}`}>Edit & resubmit</Button>}
                  {r.status === "rejected" && <RejectedNote doc={r} copyLink={r.requester_id === user?.id ? `/app/expenses-v2?copy=${r.id}` : null} testId={`exp-rejected-${i}`} />}
                  <ChainButton kind="expense" id={r.id} testId={`expense-chain-${i}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>

      <ExpenseBillDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <ChainActionDialog target={target} onClose={() => setTarget(null)} onDone={load} />
      <ExpensePayDialog exp={payOn} selfPay={!!payOn?.payee_user_id && payOn?.payee_user_id === user?.id} onClose={() => setPayOn(null)} onSaved={load} />
      <ExpensePayCorrectDialog exp={correctOn} onClose={() => setCorrectOn(null)} onSaved={load} />
      <Dialog open={!!editExp} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="exp-edit-dialog">
          {editExp && <ExpenseEditForm exp={editExp.exp} mode={editExp.mode} onSaved={() => { closeEdit(); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpenseBillDialog({ open, onOpenChange, onSaved }) {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [heads, setHeads] = useState([]);
  const [subs, setSubs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [tdsSections, setTdsSections] = useState([]);
  const [rkfState, setRkfState] = useState("Delhi");
  const [addVendorOpen, setAddVendorOpen] = useState(false);

  const [pid, setPid] = useState(""); const [hid, setHid] = useState(""); const [sid, setSid] = useState("");
  const [vid, setVid] = useState("");
  const [f, setF] = useState({
    expense_month: monthsList()[0], invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10),
    place_of_supply: "", purpose: "",
    lines: [{ description: "", qty: 0, rate: 0, amount: 0, gst_rate: 0 }],
    tds_section_code: "NONE", tds_rate: 0, tds_override_reason: "", round_off: 0,
    _ui_totals: { sub: 0, totalGst: 0, invoiceTotal: 0, tdsAmount: 0, netPayable: 0 },
  });
  const [billFiles, setBillFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/v2/expenses/my-projects"),
      api.get("/v2/payees?role=Vendor,Contractor,Landlord,Other"),
      api.get("/v2/gst-rates"),
      api.get("/v2/tds-sections"),
      api.get("/v2/org-settings"),
    ]).then(([p, v, g, t, os]) => {
      setProjects(p.data); setVendors(v.data); setGstRates(g.data); setTdsSections(t.data); setRkfState(os.data.rkf_state || "Delhi");
    });
  }, [open]);

  const [linesMsg, setLinesMsg] = useState(null);
  useEffect(() => {
    setHid(""); setSid(""); setLinesMsg(null);
    if (!pid) { setHeads([]); return; }
    api.get(`/projects/${pid}/expense-lines`).then(r => { setHeads(r.data.heads); setLinesMsg(r.data.message ? r.data : null); })
      .catch(() => { setHeads([]); setLinesMsg({ message: "Could not load budget lines for this project." }); });
  }, [pid]);
  useEffect(() => { setSubs((heads.find(h => h.id === hid) || {}).subtypes || []); setSid(""); }, [hid, heads]);

  const chosenSub = subs.find(s => s.id === sid);
  const chosenVendor = vendors.find(v => v.id === vid);
  const balance = chosenSub?.remaining || 0;
  const invTotal = f._ui_totals?.invoiceTotal || 0;
  const exceeds = invTotal > balance;

  const reset = () => {
    setPid(""); setHid(""); setSid(""); setVid(""); setBillFiles([]);
    setF({ expense_month: monthsList()[0], invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10), place_of_supply: "", purpose: "",
            lines: [{ description: "", qty: 0, rate: 0, amount: 0, gst_rate: 0 }], tds_section_code: "NONE", tds_rate: 0, tds_override_reason: "", round_off: 0,
            _ui_totals: { sub: 0, totalGst: 0, invoiceTotal: 0, tdsAmount: 0, netPayable: 0 } });
  };

  const [busy, setBusy] = useState(false);
  const [vendorErr, setVendorErr] = useState("");
  const [extraFiles, setExtraFiles] = useState([]);
  const naBillNo = !f.no_vendor_bill_no && /^(n\/?a|0+)$/i.test((f.invoice_no || "").trim());
  const submit = async () => {
    if (!pid || !hid || !sid) return toast.error("Pick project → head → sub-type");
    if (!vid) { setVendorErr("Pick a vendor"); return; }
    if (naBillNo) return toast.error("\"" + f.invoice_no + "\" is not a bill number – tick 'No vendor bill number' instead");
    if (!f.no_vendor_bill_no && !f.invoice_no.trim()) return toast.error("Vendor bill no. is required (or tick 'No vendor bill number')");
    if (!(f.lines || []).length) return toast.error("At least one line item");
    if (exceeds) return toast.error(`Invoice total ${rupee(invTotal)} exceeds balance ${rupee(balance)}`);
    const payload = {
      project_id: pid, head_id: hid, subtype_id: sid, vendor_id: vid,
      expense_month: f.expense_month, invoice_no: f.no_vendor_bill_no ? "" : f.invoice_no.trim(), no_vendor_bill_no: !!f.no_vendor_bill_no, invoice_date: f.invoice_date,
      place_of_supply: f.place_of_supply || rkfState,
      lines: f.lines.map(l => {
        const amt = parseFloat(l.amount || 0), q = parseFloat(l.qty || 0), r = parseFloat(l.rate || 0);
        const [qty, rate] = q && r ? [q, r] : amt ? [1, amt] : [q, r];
        return { description: l.description, qty, rate, amount: q && r ? Math.round(q * r * 100) / 100 : amt, gst_rate: parseFloat(l.gst_rate || 0) };
      }),
      tds_section_code: f.tds_section_code, tds_rate: parseFloat(f.tds_rate || 0),
      tds_override_reason: f.tds_override_reason, round_off: parseFloat(f.round_off || 0),
      purpose: f.purpose, bill_file_id: billFiles[0]?.id || null, attachment_file_ids: [...billFiles.slice(1), ...extraFiles].map(x => x.id), submit: true,
      ui_sub_total: f._ui_totals?.sub, ui_total_gst: f._ui_totals?.totalGst,
      ui_invoice_total: f._ui_totals?.invoiceTotal, ui_tds_amount: f._ui_totals?.tdsAmount,
      ui_net_payable: f._ui_totals?.netPayable,
    };
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/v2/expenses", payload);
      toast.success("Bill submitted");
      reset(); setExtraFiles([]); onOpenChange(false); onSaved?.();
    } catch (e) {
      if (e.response?.data?.detail_field === "vendor") {
        setVendorErr(e.response.data.detail);
        api.get("/v2/payees?role=Vendor,Contractor,Landlord,Other").then(r => setVendors(r.data || [])).catch(() => {});
      }
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const chosenProject = projects.find(p => p.id === pid);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New vendor bill</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {/* Row 1: Project / Head / Sub-type */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Project *</Label>
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger data-testid="expense-project"><SelectValue placeholder="Pick project" /></SelectTrigger>
                <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_code} · {p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Head *</Label>
              <Select value={hid} onValueChange={setHid} disabled={!pid || heads.length === 0}>
                <SelectTrigger data-testid="expense-head"><SelectValue placeholder={pid ? (heads.length === 0 ? "No heads available" : "Pick head") : "Pick project first"} /></SelectTrigger>
                <SelectContent>{heads.map(h => <SelectItem key={h.id} value={h.id} data-testid={`expense-head-opt-${h.code}`}>{h.code} · {h.name}{(h.category_name || h.category_code) ? ` (${h.category_name || h.category_code})` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sub-Type *</Label>
              <Select value={sid} onValueChange={setSid} disabled={!hid}>
                <SelectTrigger data-testid="expense-subtype"><SelectValue placeholder="Pick sub-type" /></SelectTrigger>
                <SelectContent>{subs.map(s => {
                  const bal = s.remaining || 0;
                  const empty = bal <= 0;
                  return (
                    <SelectItem key={s.id} value={s.id} disabled={empty} className={empty ? "opacity-50" : ""} data-testid={`expense-subtype-opt-${s.id}`}>
                      {s.code} · {s.name}
                      <span className={`text-xs ml-1 ${empty ? "text-slate-500" : "text-emerald-600"}`}>
                        {empty ? "Remaining ₹0" : `Remaining ${rupee(bal)}`}
                      </span>
                    </SelectItem>
                  );
                })}</SelectContent>
              </Select>
              {chosenSub && <div className="text-[11px] text-slate-500 mt-1">Working balance: <span className="font-mono">{rupee(balance)}</span></div>}
            </div>
          </div>
          {pid && linesMsg && <ExpenseLinesNotice lines={linesMsg} testId="expense-no-wb" />}

          {/* Row 2: Vendor */}
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-3" data-testid="qv-payee">
              <Label>Vendor *</Label>
              <div className="flex gap-2">
                <Select value={vid} onValueChange={(v) => { setVid(v); setVendorErr(""); }}>
                  <SelectTrigger data-testid="expense-vendor" className={vendorErr ? "border-red-500" : ""}><SelectValue placeholder="Search vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}{v.payee_role && v.payee_role !== "Vendor" ? ` (${v.payee_role})` : ""} {(v.pending_verification || v.status === "pending") && <Badge variant="outline" className="ml-1 text-[9px]">pending</Badge>}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" size="sm" variant="outline" onClick={() => setAddVendorOpen(true)} data-testid="add-vendor-inline-btn"><Plus className="w-3 h-3 mr-1" /> New</Button>
              </div>
              {vendorErr && <p className="text-xs text-red-600 mt-1" data-testid="expense-vendor-error">{vendorErr}</p>}
              {chosenVendor && (
                <div className="text-[11px] text-slate-500 mt-1 flex gap-3 flex-wrap">
                  <span>PAN: <span className="font-mono">{chosenVendor.pan || "—"}</span></span>
                  <span>GSTIN: <span className="font-mono">{chosenVendor.gstin || "—"}</span></span>
                  <span>State: {chosenVendor.state || "—"}</span>
                  <span>Type: {chosenVendor.payee_role || "Vendor"} · {chosenVendor.entity_type || chosenVendor.vendor_type}</span>
                </div>
              )}
            </div>
            <div>
              <Label>Place of supply</Label>
              <Input value={f.place_of_supply || rkfState} onChange={(e) => setF({ ...f, place_of_supply: e.target.value })} data-testid="expense-place-supply" />
            </div>
          </div>

          {/* Row 3: Expense Month / Invoice # / Invoice Date */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Expense Month *</Label>
              <Select value={f.expense_month} onValueChange={(v) => setF({ ...f, expense_month: v })}>
                <SelectTrigger data-testid="expense-month"><SelectValue /></SelectTrigger>
                <SelectContent>{monthsList().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
                <Label>Vendor bill no.{f.no_vendor_bill_no ? "" : " *"}</Label>
              <Input disabled={!!f.no_vendor_bill_no} value={f.no_vendor_bill_no ? "—" : f.invoice_no} onChange={(e) => setF({ ...f, invoice_no: e.target.value })} data-testid="expense-invoice-no" />
              <label className="flex items-center gap-1.5 text-[11px] mt-1"><input type="checkbox" checked={!!f.no_vendor_bill_no} onChange={(e) => setF({ ...f, no_vendor_bill_no: e.target.checked })} data-testid="expense-no-bill-no" /> No vendor bill number</label>
              {naBillNo && <p className="text-[11px] text-red-600" data-testid="expense-bill-no-na">“{f.invoice_no}” is not a bill number – tick “No vendor bill number” instead.</p>}
              {!naBillNo && !f.no_vendor_bill_no && f.invoice_no && (/^0+$/.test(f.invoice_no.trim()) || (!/\d/.test(f.invoice_no) && f.invoice_no.trim().length > 3) || f.invoice_no.trim().split(/\s+/).length >= 3) && <p className="text-[11px] text-amber-700" data-testid="expense-bill-no-warn">This doesn’t look like a bill number – tick “No vendor bill number” if the bill has none.</p>}
            </div>
            <div>
              <Label>Invoice Date *</Label>
              <Input type="date" value={f.invoice_date} onChange={(e) => setF({ ...f, invoice_date: e.target.value })} data-testid="expense-invoice-date" />
            </div>
          </div>

          {/* Tax block (line items + GST + TDS + summary) */}
          <TaxBlock
            vendor={chosenVendor}
            rkfState={rkfState}
            gstRates={gstRates}
            tdsSections={tdsSections}
            value={f}
            onChange={setF}
            tdsReadonly={!["Admin", "Accounts"].includes(user?.role)}
          />

          {/* Purpose + Bill */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Purpose / Remarks</Label><Textarea rows={2} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="expense-purpose" /></div>
            <InvoiceUploader label="Bill / Invoice (mandatory before verify)" files={billFiles} onChange={setBillFiles} testId="expense-bill" />
            {chosenVendor?.payee_role === "Landlord" && <InvoiceUploader label="Rent agreement (optional)" buttonText="Attach rent agreement" files={extraFiles} onChange={setExtraFiles} testId="expense-rent-agreement" />}
          </div>

          {chosenSub && (
            <div className={`text-xs px-3 py-2 rounded ${exceeds ? "bg-rose-50 text-rose-700 border border-rose-200" : "bg-slate-50 text-slate-600 border border-slate-200"}`} data-testid="expense-balance-strip">
              Available working balance: <span className="font-mono font-semibold">{rupee(balance)}</span> → Balance after this bill: <span className="font-mono font-semibold">{rupee(balance - invTotal)}</span>
              {exceeds && <span className="ml-2 font-semibold">· EXCEEDS!</span>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button onClick={submit} disabled={exceeds || busy} data-testid="submit-expense-btn">{busy ? "Saving…" : "Submit bill"}</Button>
        </DialogFooter>

        <VendorQuickAdd open={addVendorOpen} onOpenChange={setAddVendorOpen} onCreated={(v) => {
          setVendors(vs => [v, ...vs]); setVid(v.id); setAddVendorOpen(false);
        }} />
      </DialogContent>
    </Dialog>
  );
}

function VendorQuickAdd({ open, onOpenChange, onCreated }) {
  const [states, setStates] = useState([]);
  const [v, setV] = useState({ name: "", payee_role: "Vendor", vendor_type: "Company", pan: "", gstin: "", gst_registered: false, state: "Delhi", bank_account_no: "", ifsc: "" });
  useEffect(() => { if (open) api.get("/v2/states").then(r => setStates(r.data)); }, [open]);
  const save = async () => {
    if (!v.name.trim()) return toast.error("Vendor name is required");
    try {
      const r = await api.post("/v2/payees", { name: v.name, payee_role: v.payee_role, entity_type: v.vendor_type, pan: v.pan, gstin: v.gstin, gst_registered: v.gst_registered, state: v.state, ifsc: v.ifsc, account_number: v.bank_account_no });
      toast.success("Vendor added (pending verification)");
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quick-add-vendor-dialog">
        <DialogHeader><DialogTitle>Add vendor</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name *</Label><Input value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} data-testid="qv-name" /></div>
          <div>
            <Label>Payee type</Label>
            <Select value={v.payee_role} onValueChange={(x) => setV({ ...v, payee_role: x, vendor_type: x === "Landlord" ? "Individual" : v.vendor_type })}>
              <SelectTrigger data-testid="qv-role"><SelectValue /></SelectTrigger>
              <SelectContent>{["Vendor", "Contractor", "Landlord", "Other"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={v.vendor_type} onValueChange={(x) => setV({ ...v, vendor_type: x })}>
              <SelectTrigger data-testid="qv-type"><SelectValue /></SelectTrigger>
              <SelectContent>{["Individual", "HUF", "Company", "Firm/LLP", "Trust/Society", "Govt/Local Authority"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>State</Label>
            <Select value={v.state} onValueChange={(x) => setV({ ...v, state: x })}>
              <SelectTrigger data-testid="qv-state"><SelectValue /></SelectTrigger>
              <SelectContent>{states.map(s => <SelectItem key={s.code} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>PAN</Label><Input value={v.pan} onChange={(e) => setV({ ...v, pan: e.target.value.toUpperCase() })} placeholder="AAAAA9999A" data-testid="qv-pan" /></div>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" checked={v.gst_registered} onChange={(e) => setV({ ...v, gst_registered: e.target.checked })} data-testid="qv-gst-registered" id="qv-gstr" />
            <label htmlFor="qv-gstr" className="text-sm">GST Registered</label>
          </div>
          <div className="col-span-2"><Label>GSTIN {v.gst_registered && <span className="text-rose-600 text-xs">(required)</span>}</Label><Input value={v.gstin} onChange={(e) => setV({ ...v, gstin: e.target.value.toUpperCase() })} disabled={!v.gst_registered} placeholder="15 characters" data-testid="qv-gstin" /></div>
          <div><Label>Bank A/C</Label><Input value={v.bank_account_no} onChange={(e) => setV({ ...v, bank_account_no: e.target.value })} data-testid="qv-account" /></div>
          <div><Label>IFSC</Label><Input value={v.ifsc} onChange={(e) => setV({ ...v, ifsc: e.target.value.toUpperCase() })} data-testid="qv-ifsc" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} data-testid="qv-save"><Building2 className="w-4 h-4 mr-1" /> Add vendor</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
